import { createElement } from 'react';
import type { ExternalNetworkEntityKind } from '@terra/shared-contracts-external-network';
import type {
  ExternalAssetKind,
  ExternalAssetMentionData,
} from '@terra/shared-dto-chat-types';
import {
  MentionEntityMenuIcon,
  type ChatMentionsMenuItemType,
  type ChatMentionsMenuSection,
} from '@terra/ui-design-system';
import {
  getExternalAssetListLabel,
  getExternalAssetListSubtitle,
} from '../components/tabs/surface-assets/externalAssetDisplayLabels';
import { getExternalNetworkEntityKindIcon } from '../components/tabs/surface-assets/surfaceAsset.constants';

type ExternalAssetRecord = Record<string, unknown> & { id: string };

const EXTERNAL_ASSET_KIND_PARENT_LABELS: Record<ExternalAssetKind, string> = {
  NETBLOCK: 'Netblocks',
  DOMAIN: 'Domains',
  HOST: 'Hosts',
  GATEWAY: 'Gateways',
  NETWORK_SERVICE: 'Network Services',
  SOFTWARE: 'Software',
  STORAGE_SERVICE: 'Storage',
};

export const EXTERNAL_ASSET_MENTION_KINDS: ExternalNetworkEntityKind[] = [
  'DOMAIN',
  'HOST',
  'GATEWAY',
  'NETWORK_SERVICE',
  'NETBLOCK',
  'SOFTWARE',
  'STORAGE_SERVICE',
];

export function getExternalAssetMentionParentLabel(
  kind: ExternalAssetKind,
): string {
  return EXTERNAL_ASSET_KIND_PARENT_LABELS[kind];
}

function pushLine(lines: string[], label: string, value: string | undefined) {
  if (value === undefined || value.length === 0) {
    return;
  }

  lines.push(`${label}: ${value}`);
}

function readString(
  asset: ExternalAssetRecord,
  key: string,
): string | undefined {
  const value = asset[key];

  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readBooleanString(
  asset: ExternalAssetRecord,
  key: string,
): string | undefined {
  const value = asset[key];

  return typeof value === 'boolean' ? String(value) : undefined;
}

function readNumberString(
  asset: ExternalAssetRecord,
  key: string,
): string | undefined {
  const value = asset[key];

  return typeof value === 'number' ? String(value) : undefined;
}

function readStringArray(asset: ExternalAssetRecord, key: string): string[] {
  const value = asset[key];

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
}

function formatDnsRecords(asset: ExternalAssetRecord): string {
  const dnsRecords = asset.dnsRecords;

  if (!Array.isArray(dnsRecords) || dnsRecords.length === 0) {
    return 'None';
  }

  return dnsRecords
    .slice(0, 10)
    .flatMap((record) => {
      if (typeof record !== 'object' || record === null) {
        return [];
      }

      const dnsRecord = record as Record<string, unknown>;
      const rrType =
        typeof dnsRecord.rrType === 'string' ? dnsRecord.rrType : undefined;
      const rdata =
        typeof dnsRecord.rdata === 'string' ? dnsRecord.rdata : undefined;

      if (!rrType || !rdata) {
        return [];
      }

      return [`${rrType} ${rdata}`];
    })
    .join(', ');
}

export function buildExternalAssetMentionRawText({
  entityKind,
  asset,
}: {
  entityKind: ExternalNetworkEntityKind;
  asset: ExternalAssetRecord;
}): string {
  const label = getExternalAssetListLabel({ entityKind, asset });
  const lines: string[] = [
    `## Type of the entity: "ExternalAsset"`,
    `**Asset Kind:** ${entityKind}`,
    `**Label:** ${label}`,
    `**ID:** ${asset.id}`,
  ];

  switch (entityKind) {
    case 'DOMAIN': {
      pushLine(lines, '**FQDN**', readString(asset, 'fqdn'));
      pushLine(lines, '**Scope**', readString(asset, 'scope'));
      pushLine(lines, '**Source**', readString(asset, 'sourceType'));
      pushLine(lines, '**Top-level**', readBooleanString(asset, 'isTopLevel'));
      pushLine(lines, '**DNS Records**', formatDnsRecords(asset));
      break;
    }

    case 'HOST': {
      const hostnames = readStringArray(asset, 'hostnames');

      pushLine(lines, '**IP Address**', readString(asset, 'ipAddress'));
      pushLine(
        lines,
        '**Hostnames**',
        hostnames.length > 0 ? hostnames.join(', ') : undefined,
      );
      pushLine(lines, '**Cloud Provider**', readString(asset, 'cloudProvider'));
      pushLine(
        lines,
        '**Reachability**',
        readString(asset, 'reachabilityStatus'),
      );
      break;
    }

    case 'GATEWAY': {
      const ips = readStringArray(asset, 'ips');

      pushLine(lines, '**Hostname**', readString(asset, 'hostname'));
      pushLine(lines, '**Provider**', readString(asset, 'provider'));
      pushLine(lines, '**Category**', readString(asset, 'providerCategory'));
      pushLine(lines, '**IPs**', ips.length > 0 ? ips.join(', ') : undefined);
      pushLine(lines, '**Source**', readString(asset, 'sourceType'));
      break;
    }

    case 'NETWORK_SERVICE': {
      pushLine(lines, '**Port**', readNumberString(asset, 'port'));
      pushLine(lines, '**Transport**', readString(asset, 'transportProtocol'));
      pushLine(
        lines,
        '**Application Protocol**',
        readString(asset, 'applicationProtocol'),
      );
      pushLine(lines, '**TLS**', readBooleanString(asset, 'tlsEnabled'));
      pushLine(lines, '**Host IP**', readString(asset, 'hostIp'));
      pushLine(
        lines,
        '**Gateway Hostname**',
        readString(asset, 'gatewayHostname'),
      );
      pushLine(lines, '**Banner**', readString(asset, 'banner'));
      pushLine(
        lines,
        '**Reachability**',
        readString(asset, 'reachabilityStatus'),
      );
      break;
    }

    case 'NETBLOCK': {
      pushLine(lines, '**CIDR**', readString(asset, 'cidr'));
      pushLine(lines, '**Scope**', readString(asset, 'scope'));
      pushLine(lines, '**Source**', readString(asset, 'sourceType'));
      pushLine(lines, '**Version**', readNumberString(asset, 'version'));
      break;
    }

    case 'SOFTWARE': {
      pushLine(lines, '**Product**', readString(asset, 'product'));
      pushLine(lines, '**Version**', readString(asset, 'version'));
      pushLine(lines, '**Vendor**', readString(asset, 'vendor'));
      pushLine(lines, '**Software Type**', readString(asset, 'softwareType'));
      pushLine(lines, '**Domain**', readString(asset, 'domainName'));
      pushLine(lines, '**Host IP**', readString(asset, 'hostIp'));
      pushLine(lines, '**Port**', readNumberString(asset, 'port'));
      break;
    }

    case 'STORAGE_SERVICE': {
      pushLine(lines, '**Name**', readString(asset, 'name'));
      pushLine(lines, '**Endpoint**', readString(asset, 'endpointHost'));
      pushLine(lines, '**Provider**', readString(asset, 'provider'));
      pushLine(lines, '**Public Access**', readString(asset, 'publicAccess'));
      pushLine(lines, '**Region**', readString(asset, 'region'));
      break;
    }

    default: {
      const _exhaustive: never = entityKind;

      return _exhaustive;
    }
  }

  return lines.join('\n');
}

export function externalAssetToMentionMenuItem({
  asset,
  entityKind,
  surfaceId,
}: {
  asset: ExternalAssetRecord;
  entityKind: ExternalNetworkEntityKind;
  surfaceId: string;
}): ChatMentionsMenuItemType {
  const label = getExternalAssetListLabel({ entityKind, asset });
  const subtitle = getExternalAssetListSubtitle({ entityKind, asset });
  const displayLabel = subtitle ? `${label} · ${subtitle}` : label;
  const rawText = buildExternalAssetMentionRawText({ entityKind, asset });
  const Icon = getExternalNetworkEntityKindIcon(entityKind);

  const externalAsset: ExternalAssetMentionData = {
    assetKind: entityKind,
    surfaceId,
    label,
    ...(subtitle ? { subtitle } : {}),
    rawText,
  };

  return {
    id: `external-asset-${entityKind}-${asset.id}`,
    label: displayLabel,
    icon: createElement(Icon, { size: 12 }),
    type: 'child',
    mentionEntity: {
      entityType: 'ExternalAsset',
      entityId: asset.id,
      label: displayLabel,
      extraData: { externalAsset },
    },
  };
}

export function buildExternalAssetMentionParentItem({
  entityKind,
  surfaceId,
  assets,
}: {
  entityKind: ExternalNetworkEntityKind;
  surfaceId: string;
  assets: ExternalAssetRecord[];
}): ChatMentionsMenuItemType {
  const Icon = getExternalNetworkEntityKindIcon(entityKind);
  const children = assets.map((asset) =>
    externalAssetToMentionMenuItem({ asset, entityKind, surfaceId }),
  );

  return {
    id: `externalAsset-${entityKind}`,
    label: getExternalAssetMentionParentLabel(entityKind),
    icon: createElement(Icon, { size: 12 }),
    type: 'parent',
    hasSubmenu: children.length > 0,
    children,
  };
}

export function createExternalNetworkMentionBaseSections(
  assetParents: ChatMentionsMenuItemType[],
): ChatMentionsMenuSection[] {
  return [
    {
      id: 'main',
      title: '',
      items: [
        ...assetParents,
        {
          id: 'signal',
          label: 'Signals',
          icon: createElement(MentionEntityMenuIcon, { entityType: 'Signal' }),
          type: 'parent',
          children: [],
        },
        {
          id: 'finding',
          label: 'Findings',
          icon: createElement(MentionEntityMenuIcon, { entityType: 'Finding' }),
          type: 'parent',
          children: [],
        },
      ],
    },
  ];
}
